import { bind, Component, createRef, h, tag } from "omi";
import classNames from "classnames";
import isEmpty from "lodash-es/isEmpty";

import { DirectoryStore } from "@/pages/project/features/directory/store";
import { NWButton } from "@/components/atoms/nw-button";
import { NWError } from "@/components/base/nw-error";
import { NWSuspense } from "@/components/utility/nw-suspense";
import { tailwind } from "@/tailwind";

import { installMadeForYou } from "../../features/madeForYou/install";
import { useRecommendedTopics } from "../../store/queries/useRecommendedTopics";
import { useSearch } from "../../store/queries/useSearch";
import { DirectorySearchBar } from "../../components/searchBar";
import { SearchList } from "../../components/searchList";
import { TopicList } from "../../components/topicList";
import { CourseTopic, Topic } from "../../store/models";
import { DirectoryView } from "../../views";
import { NWDirectoryCourseView, type CourseSearchResult } from "./courseView";

type Props = {};

@tag("nw-directory-explore")
export class NWDirectoryExploreView extends Component<Props> {
  static css = [tailwind];

  private recommendedTopics = useRecommendedTopics();
  private search = useSearch();
  private scrollContainerRef = createRef<HTMLDivElement>();

  install() {
    this.recommendedTopics.fetch();
  }

  installed() {
    this.scrollContainerRef.current?.addEventListener("wheel", this.handleOverscroll);
  }

  uninstall() {
    this.scrollContainerRef.current?.removeEventListener("wheel", this.handleOverscroll);
  }

  @bind
  private handleOverscroll(event: WheelEvent) {
    if (!this.scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainerRef.current!;
    if ((event.deltaY < 0 && scrollTop === 0) || (event.deltaY > 0 && scrollTop + clientHeight >= scrollHeight)) {
      event.preventDefault();
    }
  }

  private onSelectTopic = (topic: Topic) => {
    const title = (topic as CourseTopic).value.metadata?.title;

    if (!isEmpty(title)) {
      DirectoryStore.setViewingCourse(title);

      import("@/services/analyticsService").then(({ useAnalyticsService }) => {
        useAnalyticsService().trackSeriesViewed(title!);
      });
    }
  };

  private onSearch = (query: string) => {
    if (!isEmpty(DirectoryStore.viewingCourse.value)) {
      DirectoryStore.setViewingCourse(undefined);
    }

    DirectoryStore.search(query);

    setTimeout(() => {
      if (this.scrollContainerRef.current) {
        this.scrollContainerRef.current.scrollTop = 0;
      }
    }, 0);
  };

  private toggleExpand = () => {
    DirectoryStore.recommendedExpanded.value = !DirectoryStore.recommendedExpanded.value;
  };

  private getActiveCourse() {
    if (isEmpty(DirectoryStore.viewingCourse.value)) {
      return undefined;
    }

    if (this.search.data.value) {
      const course = this.search.data.value.find(
        r => r.type === "course" && r.title === DirectoryStore.viewingCourse.value,
      );

      if (course) {
        return course;
      }
    }

    return undefined;
  }

  private getActiveTopic() {
    if (isEmpty(DirectoryStore.viewingCourse.value)) {
      return undefined;
    }

    return this.recommendedTopics.data.value?.find(
      r => r.type === "course" && r.value.metadata?.title === DirectoryStore.viewingCourse.value,
    );
  }

  private handleBack = () => {
    DirectoryStore.setViewingCourse(undefined);
  };

  renderOnboardingView() {
    const allError = this.recommendedTopics.error.value && this.search.error.value;
    const hasSearchTerm = !isEmpty(this.search.searchQuery.value);
    const activeCourse = this.getActiveCourse();
    const activeTopic = this.getActiveTopic();

    if (DirectoryStore.activeView.value === DirectoryView.MADE_FOR_YOU) {
      return (
        <div className="flex flex-col space-y-10 mt-6 overflow-y-auto pr-2 h-full" ref={this.scrollContainerRef}>
          <NWSuspense loadFunc={installMadeForYou} onLoadComplete={({ MadeForYou }) => <MadeForYou />} />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-10 overflow-y-auto pb-6 pr-2 h-full min-h-80" ref={this.scrollContainerRef}>
        <div class="sticky top-0 z-10">
          <DirectorySearchBar
            query={this.search.searchQuery.value}
            onChange={DirectoryStore.search}
            focusOnInstall={true}
          />
        </div>

        {allError && (
          <div className="animate-fadeInFast">
            <NWError />
          </div>
        )}

        {!allError && !activeCourse && (
          <>
            <div className={classNames("flex flex-col space-y-4 animate-fadeInFast", { hidden: hasSearchTerm })}>
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold -tracking-tight uppercase text-tertiary leading-6">Recommended</p>
                <NWButton size="fit" type="text" onClick={this.toggleExpand}>
                  {DirectoryStore.recommendedExpanded.value ? "Show less" : "Show more"}
                </NWButton>
              </div>
              <TopicList
                topics={this.recommendedTopics.data.value || []}
                onSelect={this.onSelectTopic}
                isLoading={this.recommendedTopics.isLoading.value}
                error={this.recommendedTopics.error.value}
              />
            </div>

            {hasSearchTerm && (
              <div className="animate-fadeInFast">
                <SearchList
                  results={this.search.data.value || []}
                  isLoading={this.search.isLoading.value}
                  error={this.search.error.value}
                  hasSearchTerm={true}
                />
              </div>
            )}
          </>
        )}

        {!allError && activeCourse && (
          <NWDirectoryCourseView
            searchResult={activeCourse as CourseSearchResult}
            recommendedResult={activeTopic as CourseTopic}
            onBack={this.handleBack}
          />
        )}
      </div>
    );
  }

  render() {
    const allError = this.recommendedTopics.error.value && this.search.error.value;
    const hasSearchTerm = !isEmpty(this.search.searchQuery.value);
    const activeCourse = this.getActiveCourse();
    const activeTopic = this.getActiveTopic();

    if (DirectoryStore.isOnboardingModeActive()) {
      return this.renderOnboardingView();
    }

    if (DirectoryStore.activeView.value === DirectoryView.MADE_FOR_YOU) {
      return (
        <div className="flex flex-col space-y-10 mt-6 overflow-y-auto pr-2 h-full" ref={this.scrollContainerRef}>
          <NWSuspense loadFunc={installMadeForYou} onLoadComplete={({ MadeForYou }) => <MadeForYou />} />
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-10 mt-6 overflow-y-auto pr-2 h-full" ref={this.scrollContainerRef}>
        <div class="sticky top-0 z-10">
          <DirectorySearchBar query={this.search.searchQuery.value} onChange={this.onSearch} />
        </div>

        {allError && (
          <div className="animate-fadeInFast">
            <NWError />
          </div>
        )}

        {!allError && !activeCourse && (
          <>
            <div className={classNames("flex flex-col space-y-4 animate-fadeInFast", { hidden: hasSearchTerm })}>
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold -tracking-tight uppercase text-leather leading-6">Recommended</p>
                <NWButton size="fit" type="text" onClick={this.toggleExpand}>
                  {DirectoryStore.recommendedExpanded.value ? "Show less" : "Show more"}
                </NWButton>
              </div>
              <TopicList
                topics={this.recommendedTopics.data.value || []}
                onSelect={this.onSelectTopic}
                isLoading={this.recommendedTopics.isLoading.value}
                error={this.recommendedTopics.error.value}
              />
            </div>

            <div className="flex flex-col space-y-4">
              <SearchList
                results={this.search.data.value || []}
                isLoading={this.search.isLoading.value}
                error={this.search.error.value}
                hasSearchTerm={hasSearchTerm}
              />
            </div>
          </>
        )}

        {!allError && activeCourse && (
          <NWDirectoryCourseView
            searchResult={activeCourse as CourseSearchResult}
            recommendedResult={activeTopic as CourseTopic}
            onBack={this.handleBack}
          />
        )}
      </div>
    );
  }
}
